import { formatApiErrorDetail } from "@/lib/api";
import { displayLocalDate } from "@/lib/dateHelpers";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Logo, Wordmark } from "@/components/Logo";
import { MapPin, User, CheckCircle, Warning, ImageSquare, FilePdf, House, X, ClipboardText, FileText, CalendarBlank, Signature } from "@phosphor-icons/react";
import PhotoLightbox from "@/components/PhotoLightbox";

const BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  pool: "bg-slate-200 text-slate-800",
  assigned: "bg-blue-500 text-white",
  in_progress: "bg-amber-500 text-white",
  on_site: "bg-amber-500 text-white",
  completed: "bg-green-600 text-white",
  canceled: "bg-red-500 text-white",
};
const STATUS_LABEL = {
  pool: "Waiting",
  assigned: "Scheduled",
  in_progress: "On Site",
  on_site: "On Site",
  completed: "Complete",
  canceled: "Canceled",
};

export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [openJob, setOpenJob] = useState(null);
  const [openInsp, setOpenInsp] = useState(null); // { source, id }
  const [tab, setTab] = useState("jobs"); // jobs | reports
  const [statusFilter, setStatusFilter] = useState("all"); // all / open / turn / completed
  const [landlordFilter, setLandlordFilter] = useState("all"); // pmr only
  const [q, setQ] = useState("");

  // Owner-approval action: if the email deep-links with ?action=approve&job=X, fire immediately
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get("action");
    const jobId = params.get("job");
    if (!action || !jobId || !token) return;
    if (!["approve", "decline"].includes(action)) return;
    (async () => {
      try {
        const note = action === "decline"
          ? (window.prompt("Optional note for decline (why?):", "") || "")
          : "";
        await axios.post(`${BASE}/api/public/portal/${token}/jobs/${jobId}/${action}`, { note });
        toast.success(`Work order ${action === "approve" ? "approved" : "declined"}. Thank you.`);
        // Clean the URL so a reload doesn't re-fire
        const url = new URL(window.location.href);
        url.searchParams.delete("action");
        window.history.replaceState({}, "", url.toString());
        setOpenJob(jobId);
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? `Could not ${action} — please contact your PM.`));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const load = () => {
    axios.get(`${BASE}/api/public/portal/${token}`)
      .then(r => setData(r.data))
      .catch(e => {
        const msg = e.response?.data?.detail || (e.response?.status === 404 ? "This portal link is no longer active. Ask your crew for a fresh one." : "Something went wrong.");
        setErr(msg);
      });
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const landlordList = useMemo(() => {
    if (!data || data.principal?.role !== "pmr") return [];
    const set = new Set();
    data.jobs.forEach(j => j.landlord_name && set.add(j.landlord_name));
    return Array.from(set).sort();
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let out = data.jobs;
    if (statusFilter === "open") out = out.filter(j => j.status !== "completed" && j.status !== "canceled");
    else if (statusFilter === "turn") out = out.filter(j => j.is_turn);
    else if (statusFilter === "completed") out = out.filter(j => j.status === "completed");
    if (data.principal?.role === "pmr" && landlordFilter !== "all") {
      out = out.filter(j => j.landlord_name === landlordFilter);
    }
    if (q.trim()) {
      const qL = q.toLowerCase();
      out = out.filter(j =>
        (j.property_address || "").toLowerCase().includes(qL) ||
        (j.title || "").toLowerCase().includes(qL) ||
        (j.work_order_number || "").toLowerCase().includes(qL) ||
        (j.landlord_name || "").toLowerCase().includes(qL)
      );
    }
    return out;
  }, [data, statusFilter, landlordFilter, q]);

  if (err) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
      <div className="bg-white p-8 max-w-md text-center border-2 border-[#0f172a]" data-testid="portal-error">
        <Warning weight="fill" size={40} className="mx-auto text-amber-500"/>
        <h1 className="font-display font-black text-2xl mt-3">Link Not Active</h1>
        <p className="mt-2 text-sm text-slate-600 font-medium">{err}</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500 font-bold uppercase tracking-widest">Loading…</div>;

  const roleLabel = data.principal.role === "pmr" ? "Property Manager" : "Landlord";

  return (
    <div className="min-h-screen bg-slate-100" data-testid="client-portal">
      <header className="bg-[#0f172a] text-white">
        <div className="max-w-6xl mx-auto px-4 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-white flex items-center justify-center"><Logo size={28}/></div>
            <div>
              <Wordmark dark size="text-xl"/>
              <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC]">Client Portal · {roleLabel}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-black uppercase tracking-widest text-slate-400">Signed in as</div>
            <div className="font-bold" data-testid="portal-principal-name">{data.principal.name}</div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Summary */}
        <div className="grid grid-cols-3 gap-3" data-testid="portal-summary">
          <SummaryCard label="Total Jobs" value={data.summary.total_jobs} tone="dark"/>
          <SummaryCard label="Open" value={data.summary.open_jobs} tone="cyan"/>
          <SummaryCard label="Properties" value={data.summary.property_count} tone="light"/>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex gap-1 border-b-2 border-[#0f172a]" data-testid="portal-tabs">
          <PortalTab active={tab === "jobs"} onClick={() => setTab("jobs")} testid="portal-tab-jobs">Jobs</PortalTab>
          <PortalTab active={tab === "reports"} onClick={() => setTab("reports")} testid="portal-tab-reports">
            Completed Reports
          </PortalTab>
        </div>

        {tab === "jobs" && (<>
        {/* Filters */}
        <div className="mt-6 rugged-card p-4 flex flex-wrap gap-2 items-center" data-testid="portal-filters">
          <FilterBtn active={statusFilter === "all"} onClick={() => setStatusFilter("all")} testid="filter-all">All</FilterBtn>
          <FilterBtn active={statusFilter === "open"} onClick={() => setStatusFilter("open")} testid="filter-open">Open</FilterBtn>
          <FilterBtn active={statusFilter === "turn"} onClick={() => setStatusFilter("turn")} testid="filter-turn">Turns</FilterBtn>
          <FilterBtn active={statusFilter === "completed"} onClick={() => setStatusFilter("completed")} testid="filter-completed">Completed</FilterBtn>
          {data.principal.role === "pmr" && landlordList.length > 0 && (
            <select value={landlordFilter} onChange={e => setLandlordFilter(e.target.value)} data-testid="filter-landlord"
              className="h-9 px-3 border-2 border-[#0f172a] text-xs font-bold">
              <option value="all">All Landlords</option>
              {landlordList.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          )}
          <input placeholder="Search address / WO#" value={q} onChange={e => setQ(e.target.value)} data-testid="filter-search"
            className="ml-auto h-9 px-3 border-2 border-[#0f172a] text-xs font-medium min-w-[200px] flex-1"/>
        </div>

        {/* Jobs list */}
        <div className="mt-4 space-y-2 max-h-[70vh] overflow-y-auto" data-testid="portal-jobs-list">
          {filtered.length === 0 && (
            <div className="p-8 text-center bg-white border-2 border-dashed border-slate-300 text-slate-500 font-medium">
              No jobs match your filter.
            </div>
          )}
          {filtered.map(j => (
            <div key={j.id} className="space-y-0">
            <button onClick={() => setOpenJob(j.id)} data-testid={`portal-job-${j.id}`}
              className="w-full text-left bg-white border-2 border-[#0f172a] p-4 hover:bg-slate-50 transition flex flex-wrap gap-3 items-start">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-black uppercase tracking-widest ${STATUS_STYLE[j.status] || "bg-slate-500 text-white"}`} data-testid={`portal-job-status-${j.id}`}>
                    {STATUS_LABEL[j.status] || j.status}
                  </span>
                  {j.is_turn && <span className="px-2 py-1 text-[10px] font-black uppercase tracking-widest bg-violet-600 text-white">Turn</span>}
                  {j.work_order_number && <span className="text-[10px] font-mono text-slate-500">WO# {j.work_order_number}</span>}
                </div>
                <div className="mt-2 font-black text-lg leading-tight">{j.title || "Untitled job"}</div>
                <div className="mt-1 text-sm text-slate-700 inline-flex items-center gap-1">
                  <MapPin size={13}/> {j.property_address}{j.unit ? ` · Unit ${j.unit}` : ""}
                </div>
                {data.principal.role === "pmr" && j.landlord_name && (
                  <div className="mt-1 text-xs text-slate-500 inline-flex items-center gap-1">
                    <House size={11}/> Landlord: <span className="font-bold text-slate-700">{j.landlord_name}</span>
                  </div>
                )}
              </div>
              <div className="text-right">
                {j.assigned_to_name && (
                  <div className="text-xs font-bold text-slate-700 inline-flex items-center gap-1">
                    <User size={12}/> {j.assigned_to_name}
                  </div>
                )}
                {j.scheduled_for && <div className="mt-1 text-[10px] uppercase tracking-widest font-black text-slate-500">ETA {displayLocalDate(j.scheduled_for)}</div>}
                {j.completed_at && <div className="mt-1 text-[10px] uppercase tracking-widest font-black text-green-600">Done {new Date(j.completed_at).toLocaleDateString()}</div>}
                {j.owner_approval_status && (
                  <div className={`mt-1 text-[10px] uppercase tracking-widest font-black inline-flex items-center gap-1 px-2 py-0.5 ${
                    j.owner_approval_status === "approved" ? "bg-emerald-100 text-emerald-800" :
                    j.owner_approval_status === "declined" ? "bg-red-100 text-red-800" :
                    "bg-amber-100 text-amber-800"
                  }`} data-testid={`portal-approval-status-${j.id}`}>
                    {j.owner_approval_status === "approved" ? "✓ Approved" :
                     j.owner_approval_status === "declined" ? "✗ Declined" : "Awaiting You"}
                  </div>
                )}
              </div>
            </button>
            {j.owner_approval_status === "pending" && (
              <div className="mt-2 flex gap-2" data-testid={`portal-approval-actions-${j.id}`}>
                <button onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await axios.post(`${BASE}/api/public/portal/${token}/jobs/${j.id}/approve`, {});
                    toast.success("Approved. Thanks!");
                    window.location.reload();
                  } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail ?? "Could not approve")); }
                }} data-testid={`portal-approve-${j.id}`}
                  className="flex-1 h-10 bg-emerald-600 text-white text-[11px] font-black uppercase tracking-widest hover:opacity-90 inline-flex items-center justify-center gap-1">
                  ✓ Approve
                </button>
                <button onClick={async (e) => {
                  e.stopPropagation();
                  const note = window.prompt("Optional note (why?):", "") || "";
                  try {
                    await axios.post(`${BASE}/api/public/portal/${token}/jobs/${j.id}/decline`, { note });
                    toast.success("Declined.");
                    window.location.reload();
                  } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail ?? "Could not decline")); }
                }} data-testid={`portal-decline-${j.id}`}
                  className="flex-1 h-10 bg-white text-red-700 border-2 border-red-500 text-[11px] font-black uppercase tracking-widest hover:bg-red-50">
                  Decline
                </button>
              </div>
            )}
            </div>
          ))}
        </div>
        </>)}

        {tab === "reports" && (
          <PortalReportsTab token={token} onOpen={(source, id) => setOpenInsp({ source, id })}/>
        )}
      </div>

      {openJob && <JobDrawer token={token} jobId={openJob} onClose={() => setOpenJob(null)}/>}
      {openInsp && <PortalReportDrawer token={token} source={openInsp.source} id={openInsp.id} onClose={() => setOpenInsp(null)}/>}
    </div>
  );
}

function PortalTab({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`h-11 px-5 font-black uppercase tracking-widest text-xs border-2 border-b-0 ${
        active ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0f172a]"
      }`}>
      {children}
    </button>
  );
}

const CI_KIND_COLOR = {
  move_in: "bg-emerald-600", move_out: "bg-rose-600",
  turn: "bg-violet-600", periodic: "bg-cyan-600", custom: "bg-slate-600",
};
const CI_COND_COLOR = {
  excellent: "bg-emerald-600", great: "bg-emerald-600",
  good: "bg-sky-600", fair: "bg-amber-500",
  poor: "bg-red-600", broken: "bg-red-800", damaged: "bg-red-900",
  na: "bg-slate-400", "": "bg-slate-300",
};

function PortalReportsTab({ token, onOpen }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState("all");

  useEffect(() => {
    axios.get(`${BASE}/api/public/portal/${token}/completed-inspections`)
      .then(r => setRows(r.data.inspections || []))
      .catch(e => setErr(e.response?.data?.detail || "Could not load reports"));
  }, [token]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    let out = rows;
    if (kindFilter !== "all") out = out.filter(r => r.kind === kindFilter);
    if (q.trim()) {
      const qL = q.toLowerCase();
      out = out.filter(r =>
        (r.property_address || "").toLowerCase().includes(qL) ||
        (r.tenant_name || "").toLowerCase().includes(qL) ||
        (r.landlord_name || "").toLowerCase().includes(qL)
      );
    }
    return out;
  }, [rows, q, kindFilter]);

  if (err) return <div className="mt-6 text-red-600 font-bold" data-testid="portal-reports-error">{err}</div>;
  if (!rows) return <div className="mt-6 text-slate-500 font-bold uppercase text-xs text-center">Loading…</div>;

  return (
    <div className="mt-4" data-testid="portal-reports-tab">
      <div className="rugged-card p-4 flex flex-wrap gap-2 items-center">
        <FilterBtn active={kindFilter === "all"} onClick={() => setKindFilter("all")} testid="portal-rk-all">All</FilterBtn>
        <FilterBtn active={kindFilter === "move_in"} onClick={() => setKindFilter("move_in")} testid="portal-rk-movein">Move-In</FilterBtn>
        <FilterBtn active={kindFilter === "move_out"} onClick={() => setKindFilter("move_out")} testid="portal-rk-moveout">Move-Out</FilterBtn>
        <FilterBtn active={kindFilter === "turn"} onClick={() => setKindFilter("turn")} testid="portal-rk-turn">Turn</FilterBtn>
        <FilterBtn active={kindFilter === "periodic"} onClick={() => setKindFilter("periodic")} testid="portal-rk-periodic">Periodic</FilterBtn>
        <input placeholder="Search address / tenant" value={q} onChange={e => setQ(e.target.value)} data-testid="portal-report-search"
          className="ml-auto h-9 px-3 border-2 border-[#0f172a] text-xs font-medium min-w-[200px] flex-1"/>
      </div>

      {rows.length === 0 && (
        <div className="mt-4 p-8 text-center bg-white border-2 border-dashed border-slate-300 text-slate-500 font-medium" data-testid="portal-reports-empty">
          No completed inspection reports yet.
        </div>
      )}
      {rows.length > 0 && filtered.length === 0 && (
        <div className="mt-4 text-center text-slate-500 font-bold uppercase text-xs">No reports match your filters.</div>
      )}

      <div className="mt-3 space-y-2 max-h-[70vh] overflow-y-auto" data-testid="portal-reports-list">
        {filtered.map(r => (
          <button key={`${r.source}:${r.id}`} onClick={() => onOpen(r.source, r.id)}
            data-testid={`portal-report-${r.source}-${r.id}`}
            className="w-full text-left bg-white border-2 border-[#0f172a] p-4 hover:bg-slate-50 transition flex flex-wrap gap-3 items-center">
            <span className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest text-white ${CI_KIND_COLOR[r.kind] || "bg-slate-600"}`}>
              {r.kind_label}
            </span>
            <div className="flex-1 min-w-[240px]">
              <div className="font-black text-sm">{r.property_address}{r.unit ? ` · Unit ${r.unit}` : ""}</div>
              <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap items-center gap-x-3">
                {r.tenant_name && <span className="inline-flex items-center gap-1"><User size={11}/> {r.tenant_name}</span>}
                {r.landlord_name && <span className="inline-flex items-center gap-1"><House size={11}/> {r.landlord_name}</span>}
              </div>
            </div>
            <div className="text-right">
              <div className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-700">
                <CheckCircle weight="fill" size={12}/> Done
              </div>
              {r.completed_at && <div className="text-[11px] text-slate-500 mt-0.5">{new Date(r.completed_at).toLocaleDateString()}</div>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function PortalReportDrawer({ token, source, id, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${BASE}/api/public/portal/${token}/completed-inspections/${source}/${id}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Could not load report"));
  }, [token, source, id]);

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose} data-testid="portal-report-drawer">
      <div className="bg-white w-full max-w-4xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-[#0f172a] text-white px-5 py-4 flex items-center justify-between">
          <div className="font-display font-black text-lg uppercase">Finished Report</div>
          <button onClick={onClose} className="p-1 hover:bg-white/20" data-testid="portal-report-close"><X size={22}/></button>
        </div>
        {err && <div className="p-6 text-red-600 font-bold" data-testid="portal-report-err">{err}</div>}
        {!err && !data && <div className="p-6 text-slate-500 font-bold uppercase tracking-widest">Loading…</div>}
        {data && <PortalFinishedReport data={data}/>}
      </div>
    </div>
  );
}

function PortalFinishedReport({ data }) {
  const [lightbox, setLightbox] = useState(null);
  return (
    <div className="p-5 space-y-6" data-testid="portal-finished-report">
      <div className="border-b-4 border-[#0f172a] pb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest text-white ${CI_KIND_COLOR[data.kind] || "bg-slate-600"}`}>
            {data.kind_label}
          </span>
          <span className="px-2 py-1 text-[10px] font-black uppercase tracking-widest bg-emerald-600 text-white inline-flex items-center gap-1">
            <CheckCircle weight="fill" size={12}/> Completed
          </span>
          {data.work_order_number && <span className="text-xs font-mono text-slate-500">WO# {data.work_order_number}</span>}
        </div>
        <h2 className="font-display font-black text-2xl sm:text-3xl mt-3 leading-tight">
          {data.property_address}{data.unit ? ` · Unit ${data.unit}` : ""}
        </h2>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-700">
          {data.tenant_name && <div><span className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Tenant:</span> <b>{data.tenant_name}</b></div>}
          {data.landlord_name && <div><span className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Landlord:</span> <b>{data.landlord_name}</b></div>}
          {data.assigned_to_name && <div><span className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Inspector:</span> <b>{data.assigned_to_name}</b></div>}
          {data.signed_by && <div className="inline-flex items-center gap-1"><Signature size={13}/><span className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Signed:</span> <b>{data.signed_by}</b></div>}
          {data.completed_at && <div className="inline-flex items-center gap-1"><CalendarBlank size={13}/><span className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Completed:</span> <b>{new Date(data.completed_at).toLocaleString()}</b></div>}
        </div>
      </div>

      {(data.phases || []).map((phase, pi) => (
        <section key={pi} data-testid={`portal-phase-${phase.phase_key}`}>
          <div className="flex items-center gap-2 mb-3">
            <div className={`h-1 flex-1 ${phase.phase_key === "move_out" ? "bg-rose-600" : phase.phase_key === "move_in" ? "bg-emerald-600" : "bg-cyan-600"}`}/>
            <h3 className="font-display font-black uppercase tracking-widest text-sm">{phase.phase_label}</h3>
            <div className={`h-1 flex-1 ${phase.phase_key === "move_out" ? "bg-rose-600" : phase.phase_key === "move_in" ? "bg-emerald-600" : "bg-cyan-600"}`}/>
          </div>
          {(phase.rooms || []).length === 0 && <div className="text-xs text-slate-500 italic">No room data recorded.</div>}
          <div className="space-y-3">
            {(phase.rooms || []).map((room, ri) => (
              <div key={ri} className="border-2 border-slate-200 p-3" data-testid={`portal-room-${phase.phase_key}-${ri}`}>
                <div className="font-display font-black text-base uppercase">{room.name}</div>
                <div className="mt-2 space-y-3">
                  {(room.items || []).map((item, ii) => {
                    const cond = (item.condition || "").toLowerCase();
                    const condColor = CI_COND_COLOR[cond] || "bg-slate-300";
                    return (
                      <div key={ii} className="border-t pt-2 first:border-t-0 first:pt-0"
                        data-testid={`portal-item-${phase.phase_key}-${ri}-${ii}`}>
                        <div className="flex items-start gap-2 flex-wrap">
                          <div className="flex-1 min-w-[180px] text-sm font-bold">{item.label}</div>
                          <span className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest text-white ${condColor}`}>
                            {cond ? cond : "Not Answered"}
                          </span>
                        </div>
                        {item.notes && <div className="mt-1 text-xs italic text-slate-700 whitespace-pre-wrap">{item.notes}</div>}
                        {(item.photos || []).length > 0 && (
                          <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                            {item.photos.map((url, pIdx) => (
                              <button key={pIdx} type="button"
                                onClick={() => setLightbox({
                                  photos: item.photos,
                                  index: pIdx,
                                  label: `${room.name} · ${phase.phase_label}`,
                                })}
                                data-testid={`portal-photo-${phase.phase_key}-${ri}-${ii}-${pIdx}`}
                                className="focus:outline-none focus:ring-2 focus:ring-[#00C4CC]">
                                <img src={url} alt="" className="h-24 w-24 object-cover border-2 border-[#0f172a] flex-shrink-0 hover:opacity-80 transition"/>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
      {lightbox && (
        <PhotoLightbox
          photos={lightbox.photos}
          startIndex={lightbox.index}
          label={lightbox.label}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, tone }) {
  const cls = tone === "dark" ? "bg-[#0f172a] text-white"
    : tone === "cyan" ? "bg-[#00C4CC] text-white"
    : "bg-white border-2 border-[#0f172a]";
  return (
    <div className={`${cls} p-4`} data-testid={`summary-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="text-[10px] font-black uppercase tracking-widest opacity-80">{label}</div>
      <div className="mt-1 font-display font-black text-3xl">{value}</div>
    </div>
  );
}

function FilterBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`h-9 px-4 font-black uppercase tracking-widest text-xs border-2 ${active ? "bg-[#0f172a] text-white border-[#0f172a]" : "border-[#0f172a] text-slate-700 hover:bg-slate-50"}`}>
      {children}
    </button>
  );
}

function JobDrawer({ token, jobId, onClose }) {
  const [job, setJob] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${BASE}/api/public/portal/${token}/jobs/${jobId}`)
      .then(r => setJob(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Could not load job"));
  }, [token, jobId]);

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose} data-testid="portal-job-drawer">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-[#0f172a] text-white px-5 py-4 flex items-center justify-between">
          <div className="font-display font-black text-lg uppercase">Job Details</div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="portal-drawer-close"><X size={22}/></button>
        </div>
        {err && <div className="p-6 text-red-600 font-bold">{err}</div>}
        {!err && !job && <div className="p-6 text-slate-500 font-bold uppercase tracking-widest">Loading…</div>}
        {job && (
          <div className="p-6 space-y-5">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest ${STATUS_STYLE[job.status] || "bg-slate-500 text-white"}`}>
                  {STATUS_LABEL[job.status] || job.status}
                </span>
                {job.is_turn && <span className="px-2 py-1 text-[10px] font-black uppercase tracking-widest bg-violet-600 text-white">Turn</span>}
                {job.work_order_number && <span className="text-xs font-mono text-slate-500">WO# {job.work_order_number}</span>}
              </div>
              <h2 className="mt-2 font-display font-black text-2xl leading-tight">{job.title || "Untitled job"}</h2>
              <div className="mt-1 text-sm text-slate-700 inline-flex items-center gap-1">
                <MapPin size={14}/> {job.property_address}{job.unit ? ` · Unit ${job.unit}` : ""}
              </div>
              {job.assigned_to_name && (
                <div className="mt-1 text-sm text-slate-600 inline-flex items-center gap-1">
                  <User size={13}/> Worker: <span className="font-bold">{job.assigned_to_name}</span>
                </div>
              )}
            </div>

            {job.description && (
              <Section title="Description">
                <p className="text-sm whitespace-pre-wrap text-slate-700">{job.description}</p>
              </Section>
            )}

            {(job.completion_notes || job.work_performed_polished) && (
              <Section title="Completion Notes" icon={<CheckCircle weight="fill" size={16} className="text-green-600"/>}>
                <p className="text-sm whitespace-pre-wrap text-slate-700">{job.work_performed_polished || job.completion_notes}</p>
              </Section>
            )}

            {job.chargebacks && job.chargebacks.length > 0 && (
              <Section title={`Chargebacks (${job.chargebacks.length})`} icon={<ClipboardText weight="fill" size={16} className="text-red-600"/>}>
                <div className="space-y-3" data-testid="portal-chargebacks">
                  {job.chargebacks.map((cb, i) => (
                    <div key={i} className="border-2 border-red-200 bg-red-50 p-3" data-testid={`portal-chargeback-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black uppercase tracking-widest text-red-700 bg-red-100 px-2 py-0.5">Tenant Charge</span>
                        {cb.room && <span className="text-xs font-bold text-slate-700">{cb.room}</span>}
                      </div>
                      <div className="mt-1 font-bold text-sm">{cb.title || cb.category}</div>
                      {cb.condition && <div className="text-xs text-slate-600 mt-1">{cb.condition}</div>}
                      {cb.photos && cb.photos.length > 0 && <PhotoStrip paths={cb.photos} token={token}/>}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {(job.before_photos?.length > 0 || job.after_photos?.length > 0) && (
              <Section title="Photos" icon={<ImageSquare weight="fill" size={16}/>}>
                {job.before_photos.length > 0 && (
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Before ({job.before_photos.length})</div>
                    <PhotoStrip paths={job.before_photos} token={token}/>
                  </div>
                )}
                {job.after_photos.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">After ({job.after_photos.length})</div>
                    <PhotoStrip paths={job.after_photos} token={token}/>
                  </div>
                )}
              </Section>
            )}

            {job.has_turn_pdf && (
              <a href={`${BASE}/api/jobs/${job.id}/pdf?auth=`} target="_blank" rel="noreferrer" data-testid="portal-turn-pdf"
                className="block w-full h-12 bg-[#0f172a] text-white font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 hover:opacity-90">
                <FilePdf size={16}/> Download Turn PDF
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <section>
      <h3 className="font-display font-black text-sm uppercase tracking-widest text-slate-500 flex items-center gap-1.5">{icon}{title}</h3>
      <div className="mt-2">{children}</div>
    </section>
  );
}

function PhotoStrip({ paths, token }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {paths.map((p, i) => {
        const url = typeof p === "string" && p.startsWith("http") ? p : `${BASE}/api/files/${p}?auth=`;
        return <img key={i} src={url} alt="" className="h-24 w-24 object-cover border-2 border-[#0f172a] flex-shrink-0"/>;
      })}
    </div>
  );
}
